import * as _ from 'underscore';

import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DecimalPipe } from '@angular/common';

import { EmitterService } from '../../../../core/utility/services/emitter.service';
import { ColorPalette } from '../../../analytics-core/interfaces/partner.interface';
import { DownloadConfig } from '../../../analytics-core/interfaces/widget.interface';
import { ColorUtilityService } from '../../../analytics-core/services/color.utility.service';
import { EVENT_EMITTERS, PATHS } from '../../../analytics/constants/analytics.constants';
import { AnalyticsDataModel } from '../../../analytics/models/analytics.datamodel';
import { AnalyticsService } from '../../../analytics/services/analytics.service';
import { WidgetCore } from '../../interfaces/widget.core.interface';
import { DeviceTableComponent } from '../device-table/device-table.component';
import { UtilityService } from '../../../../core/utility/services/utility.service';

@Component({
	selector: 'app-social-demographics-v6',
	styleUrls: ['./social-demographics-v6.component.css'],
	templateUrl: './social-demographics-v6.component.html',
	providers: [DecimalPipe]
})
export class SocialDemographicsV6Component
	extends DeviceTableComponent
	implements OnInit, AfterViewInit, WidgetCore, OnDestroy {
	@Input() downloadconfig: string;
	@Input() legend: any;
	@Input() metricsarray: any;
	@ViewChild('chartElement') chartElement: ElementRef;
	public sectiontitle: string;
	public key: string;
	public colors: string;
	public groupby: string;
	public showLoad = true;
	public hasData = true;
	public donutColors: string[] = [];
	public colorPalette: ColorPalette;
	public reloadSubscription;
	public widgetDataSubscription;
	public downloadConfiguration: DownloadConfig;
	public legendData: any;
	public chartDataObj = [];
	public donutArray = [];
	public currentLegend: any;
	public currentChartData: any;
	public metricsArray = [];
	public totalsArray = [];
	public cssClasses = {
		sortAscending: 'fa fa-caret-up',
		sortDescending: 'fa fa-caret-down'
	};

	constructor(
		protected analyticsService: AnalyticsService,
		protected colorUtilityService: ColorUtilityService,
		protected analyticsDataModel: AnalyticsDataModel,
		private _decimalPipe: DecimalPipe
	) {
		super(analyticsService, colorUtilityService, analyticsDataModel);
	}

	ngOnInit(): void {
		this.downloadConfiguration = JSON.parse(decodeURI(this.downloadconfig));
		this.legend = JSON.parse(decodeURI(this.legend));
		this.metricsArray = JSON.parse(decodeURI(this.metricsarray));
		this.processLegendData();
		this.requestData();
	}

	ngAfterViewInit(): void {
		this.colorPalette = this.analyticsDataModel.partner.colorPalette;
		this.donutColors = this.colors.split(',');
		this.setHeaders();
	}

	processLegendData(): void {
		this.currentLegend = this.legend[0].value;
	}

	updateLegendData(legend: any): void {
		this.currentLegend = legend;
		this.currentChartData = this.chartDataObj[this.currentLegend];
		//update the table headers/columns
		this.setHeaders();
	}

	requestData(): void {
		this.hasData = true;
		this.showLoad = true;
		this.widgetDataSubscription = this.analyticsService.processCustomServiceRequest(this.key).subscribe((response: any) => {
			this.showLoad = false;
			if (response.success && response.data.length > 0) {
				this.hasData = true;
				const temp = JSON.parse(JSON.stringify(response.data[2]));
				response.data[2] = response.data[1];
				response.data[1] = temp;
				this.legendData = ['impressions', 'clicks', 'engagements'];
				this.processResponseData(response.data);
			} else {
				this.hasData = false;
			}
		});
	}

	getReducedValues(data: any, gender: string, prop: string): any {
		return _.where(data, { gender }).reduce(
			// tslint:disable-next-line: no-parameter-reassignment
			(acc: any, item: any) => (acc = acc + (item[prop] ? parseInt(item[prop], 10) : 0)),
			0
		);
	}

	getTotalMetricValues(data: any, prop: string): any {
		// tslint:disable-next-line: no-parameter-reassignment
		return _.pluck(data, prop).reduce((acc, item: any) => (acc = acc + (item ? parseInt(item, 10) : 0)), 0);
	}

	processResponseData(responseData: any): void {
		const genderArray = ['male', 'female', 'unknown'];
		const metricObj: any = {};

		this.metricsArray.forEach((metric) => {
			metricObj[metric] = genderArray.map((gender) => ({
				series: [
					{
						data: [
							{
								color: this.colorPalette.primaryColor,
								innerRadius: '60%',
								radius: '100%',
								y:
									(this.getReducedValues(responseData, gender, metric) /
										this.getTotalMetricValues(responseData, metric)) *
									100 // percentage of individual value WRT to total
							}
						],
						name: metric
					}
				],
				title: gender,
				value: this.getReducedValues(responseData, gender, metric)
			}));
		});

		this.chartDataObj = metricObj;
		this.currentChartData = this.chartDataObj['impressions'];
		this.hasData = responseData.length > 0;

		this.tableData = this.mapResponseDataFortable(responseData);
	}

	ngOnDestroy(): void {
		if (this.reloadSubscription) {
			this.reloadSubscription.unsubscribe();
		}
		if (this.widgetDataSubscription) {
			this.widgetDataSubscription.unsubscribe();
		}
	}

	downloadCSV(): void {
		this.analyticsService.downloadCSV(
			this.downloadConfiguration.csvQueryKey ? this.downloadConfiguration.csvQueryKey : this.key,
			this.downloadConfiguration.fileName
		);
	}

	mapResponseDataFortable(data: any): any[] {
		let response = [];
		data.forEach((item: any) => {
			if (!response[item.gender]) {
				response[item.gender] = {};
			}
			let age_group = 'group_' + item.age_group.replace(/-|\+/g, '_');
			let impressions = !UtilityService.isUndefinedOrNull(item.impressions)
				? UtilityService.toInt(item.impressions)
				: 0;
			let clicks = !UtilityService.isUndefinedOrNull(item.clicks) ? UtilityService.toInt(item.clicks) : 0;
			let engagements = !UtilityService.isUndefinedOrNull(item.engagements)
				? UtilityService.toInt(item.engagements)
				: 0;
			response[item.gender]['gender'] = item.gender;
			response[item.gender][age_group + '_impressions'] = impressions;
			response[item.gender][age_group + '_clicks'] = clicks;
			response[item.gender][age_group + '_engagements'] = engagements;
			if (
				UtilityService.isUndefinedOrNull(response[item.gender]['total_impressions']) ||
				UtilityService.isUndefinedOrNull(response[item.gender]['total_clicks']) ||
				UtilityService.isUndefinedOrNull(response[item.gender]['total_engagements'])
			) {
				response[item.gender]['total_impressions'] = 0;
				response[item.gender]['total_clicks'] = 0;
				response[item.gender]['total_engagements'] = 0;
			}
			response[item.gender]['total_impressions'] += impressions;
			response[item.gender]['total_clicks'] += clicks;
			response[item.gender]['total_engagements'] += engagements;
		});

		response = _.sortBy(Object.values(response), (item) => {
			if (item.gender === 'male') {
				return 1;
			} else if (item.gender === 'female') {
				return 2;
			}
			return 3;
		});

		return response;
	}

	// summary function for ngx-datatable columns to compute the total
	private sumRecords(cells: number[]): number {
		return cells.reduce((sum, cell) => (sum += cell), 0);
	}

	//function for formatting the number value
	decimalNumberPipe(format: any = '1.0-0', prepand: string = '') {
		return {
			transform: (value: any) => {
				// fix for ngx-datatable issue with pipe and summary used together
				value = value.toString().replace(/,/g, '');
				return value ? this._decimalPipe.transform(value, format) + prepand : 0;
			}
		};
	}

	//setting header depending on the user selection impressions/clicks
	setHeaders() {
		let columns = [
			{
				cellClass: 'gender',
				headerClass: 'display-deviceHeader',
				name: 'GENDER',
				prop: 'gender',
				sortable: false,
				resizeable: false,
				summaryFunc: () => {
					return 'Total';
				}
			},
			{
				cellClass: 'display-impressions',
				headerClass: 'display-impressionsHeader',
				name: '13-17',
				prop: 'group_13_17_impressions',
				sortable: false,
				resizeable: false,
				pipe: this.decimalNumberPipe(),
				summaryFunc: (cells) => this.sumRecords(cells)
			},
			{
				cellClass: 'display-impressions',
				headerClass: 'display-impressionsHeader',
				name: '18-24',
				prop: 'group_18_24_impressions',
				pipe: this.decimalNumberPipe(),
				sortable: false,
				resizeable: false,
				summaryFunc: (cells) => this.sumRecords(cells)
			},
			{
				cellClass: 'display-impressions',
				headerClass: 'display-impressionsHeader',
				name: '25-34',
				prop: 'group_25_34_impressions',
				pipe: this.decimalNumberPipe(),
				sortable: false,
				resizeable: false,
				summaryFunc: (cells) => this.sumRecords(cells)
			},
			{
				cellClass: 'display-impressions',
				headerClass: 'display-impressionsHeader',
				name: '35-44',
				prop: 'group_35_44_impressions',
				pipe: this.decimalNumberPipe(),
				sortable: false,
				resizeable: false,
				summaryFunc: (cells) => this.sumRecords(cells)
			},
			{
				cellClass: 'display-impressions',
				headerClass: 'display-impressionsHeader',
				name: '45-54',
				prop: 'group_45_54_impressions',
				pipe: this.decimalNumberPipe(),
				sortable: false,
				resizeable: false,
				summaryFunc: (cells) => this.sumRecords(cells)
			},
			{
				cellClass: 'display-impressions',
				headerClass: 'display-impressionsHeader',
				name: '55-64',
				prop: 'group_55_64_impressions',
				pipe: this.decimalNumberPipe(),
				sortable: false,
				resizeable: false,
				summaryFunc: (cells) => this.sumRecords(cells)
			},
			{
				cellClass: 'display-impressions',
				headerClass: 'display-impressionsHeader',
				name: '65+',
				prop: 'group_65__impressions',
				pipe: this.decimalNumberPipe(),
				sortable: false,
				resizeable: false,
				summaryFunc: (cells) => this.sumRecords(cells)
			},
			{
				cellClass: 'display-impressions',
				headerClass: 'display-impressionsHeader',
				name: 'ALL',
				prop: 'total_impressions',
				pipe: this.decimalNumberPipe(),
				sortable: false,
				resizeable: false,
				summaryFunc: (cells) => this.sumRecords(cells)
			}
		];

		this.currentLegend = this.currentLegend ? this.currentLegend : 'impressions';
		columns.forEach((col) => {
			if (this.currentLegend == 'impressions') {
				col.prop = col.prop.toString().includes('clicks')
					? col.prop.toString().replace('clicks', this.currentLegend)
					: col.prop.toString().includes('engagements')
					? col.prop.toString().replace('engagements', this.currentLegend)
					: col.prop.toString();
			} else if (this.currentLegend == 'clicks') {
				col.prop = col.prop.toString().includes('impressions')
					? col.prop.toString().replace('impressions', this.currentLegend)
					: col.prop.toString().includes('engagements')
					? col.prop.toString().replace('engagements', this.currentLegend)
					: col.prop.toString();
			} else if (this.currentLegend == 'engagements') {
				col.prop = col.prop.toString().includes('impressions')
					? col.prop.toString().replace('impressions', this.currentLegend)
					: col.prop.toString().includes('clicks')
					? col.prop.toString().replace('clicks', this.currentLegend)
					: col.prop.toString();
			}
		});

		this.columns = columns;
	}
}
